<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { Modal, useModalStack } from '@gat-ship/vue-components'
  import { CreateStakeholderProvider } from '@gat-ship/stakeholder-create'
  import CreatePortcallStepFlow from './create-portcall-provider/step-flow/CreatePortcallStepFlow.vue'

  /**
   * Global modal-stack host for the keyboard-first create-portcall step flow.
   * Registered as the `create-portcall` kind (see `register-app-modals.ts`), so
   * the wizard opens via `useModalStack().open('create-portcall')` from anywhere
   * and is deep-linkable through `?modals=create-portcall`.
   *
   * Open state is derived from the central stack (presence of this kind's entry)
   * rather than a local ref. The wizard lives in the dialog's lazily-mounted
   * content, so its form/datasource setup — and the wrapping
   * `CreateStakeholderProvider` it needs for the "Add client" affordance — only
   * runs while the modal is open.
   */
  const KIND = 'create-portcall'

  const stack = useModalStack()

  const entry = computed(() =>
    stack.entries.value.find((item) => item.kind === KIND)
  )

  const open = computed({
    get: () => entry.value != null,
    set: (value) => {
      if (!value) stack.close(KIND)
    }
  })

  // Viewport-filling mode, toggled from the wizard header. The ref lives on this
  // always-mounted stack host (not the lazily-mounted wizard), so the choice
  // persists across open/close within a session — matching AdministrationModal.
  const isFullScreen = ref(false)
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    hide-close-button
    full-screen-mobile
    :max-width="880"
    :full-screen="isFullScreen"
  >
    <CreateStakeholderProvider>
      <CreatePortcallStepFlow
        :full-screen="isFullScreen"
        @close="open = false"
        @toggle-fullscreen="isFullScreen = !isFullScreen"
      />
    </CreateStakeholderProvider>
  </Modal>
</template>
